import { FC, Fragment } from "react";
import { useWebTranslation } from "translations";
import { SelectablePaymentMethodEnum } from "config/site";
import TrustlyComponent from "./trustly/TrustlyComponent";
import { Typography } from "ui-kit/Typography";
import { FlexCol, FlexRow } from "ui-kit/Layout/Flex";
import styles from "./PaymentMethods.module.css";
import Gutter from "ui-kit/Gutter";
import { PaymentMethodState } from "./usePaymentMethodState";
import TrustlyManualComponent from "./trustly/TrustlyManualComponent";
import StripeComponent from "./stripe/StripeComponent";
import Colors from "ui-kit/colors";
import BankLogos from "./trustly/BankLogos";
import PaymentMethod from "./PaymentMethod";

type PaymentMethodProps = {
  billingDetails: { name: string; email: string; phone: string };
  selectablePaymentMethods: SelectablePaymentMethodEnum[];
  paymentMethod: SelectablePaymentMethodEnum;
  setPaymentMethod: (paymentMethod: SelectablePaymentMethodEnum) => void;
  paymentMethodState: PaymentMethodState;
  setPaymentMethodState: ({ isValid, data }: PaymentMethodState) => void;
  isPayout?: boolean;
  noTitle?: boolean;
};

const PaymentMethods: FC<PaymentMethodProps> = ({
  billingDetails,
  selectablePaymentMethods,
  paymentMethod,
  setPaymentMethod,
  paymentMethodState,
  setPaymentMethodState,
  isPayout,
  noTitle,
}) => {
  const { t } = useWebTranslation();

  const items = selectablePaymentMethods.map((pm) => {
    if (pm === SelectablePaymentMethodEnum.Stripe) {
      return (
        <StripeComponent
          billingDetails={billingDetails}
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          setPaymentMethodState={setPaymentMethodState}
        />
      );
    } else if (pm === SelectablePaymentMethodEnum.TrustlyMddManual) {
      return (
        <PaymentMethod
          type={SelectablePaymentMethodEnum.TrustlyMddManual}
          title={t("payment_methods.direct_debit")}
          icon="trustly-bank"
          logo={<BankLogos />}
          description={
            isPayout
              ? t("payment_methods.direct_debit_payout_description")
              : t("payment_methods.direct_debit_description")
          }
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
        >
          <TrustlyManualComponent
            paymentMethod={paymentMethod}
            paymentMethodState={paymentMethodState}
            setPaymentMethodState={setPaymentMethodState}
          />
        </PaymentMethod>
      );
    } else {
      return (
        <PaymentMethod
          type={pm}
          title={t("payment_methods.direct_debit")}
          icon="trustly-bank"
          logo={<BankLogos />}
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
        >
          <TrustlyComponent
            paymentMethod={paymentMethod}
            setPaymentMethodState={setPaymentMethodState}
          />
        </PaymentMethod>
      );
    }
  });

  return (
    <>
      {!noTitle && (
        <>
          <Typography variant="body2" color="grey500">
            <strong>{t("policy.payment_method")}</strong>
          </Typography>
          <Gutter size={3} />
        </>
      )}
      <FlexCol className={styles.container}>
        {items.map((item, index) => (
          <Fragment key={index}>
            {item}
            {item !== items[items.length - 1] && (
              <FlexRow justifyContent="center" alignItems="center">
                <div
                  style={{
                    borderTop: `1px solid ${Colors.grey300}`,
                    width: "100%",
                  }}
                />
                <Gutter size={2} horizontal />
                <Typography color="grey500" style={{ whiteSpace: "nowrap" }}>
                  {t(
                    isPayout
                      ? "payment_methods.separator_payout"
                      : "payment_methods.separator"
                  )}
                </Typography>
                <Gutter size={2} horizontal />
                <div
                  style={{
                    borderTop: `1px solid ${Colors.grey300}`,
                    width: "100%",
                  }}
                />
              </FlexRow>
            )}
          </Fragment>
        ))}
      </FlexCol>
    </>
  );
};

export default PaymentMethods;
